import { ChangeEvent, RefObject, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import SmileEmoji from 'shared/icons/SmileEmoji'
import EmojiPicker from 'emoji-picker-react'
import Loader from 'App/Loader'
import SendBold from 'shared/icons/SendBold'
import AttachFile from 'shared/icons/AttachFile'
import styles from "./tournament-input.module.scss";

interface MessageInputProps {
  className?: string
  id?: string
  placeholder?: string
  inputText:string
  setInputText: React.Dispatch<React.SetStateAction<string>>
  sendMessageLoader: boolean
  onSendMessage: (text: string) => void
  onFileSelect?: (files: File[]) => void
  uploadRef: RefObject<HTMLInputElement | null>
  matchChatModeratorHelp?: boolean;
}

const MENTION = "@moderator";

function getActiveMentionQuery(text: string, cursorPos: number) {
  const before = text.slice(0, cursorPos);
  const atIndex = before.lastIndexOf("@");

  if (atIndex < 0) return null;

  const afterAt = before.slice(atIndex + 1);

  if (/\s/.test(afterAt)) return null;

  const q = afterAt.toLowerCase();

  if (q !== "" && !"moderator".startsWith(q)) return null;

  return { atIndex, query: q };
}

function applyMentionInsert(
  fullText: string,
  cursorPos: number,
  atIndex: number,
  mention: string
) {
  const next = fullText.slice(cursorPos);
  const insert = `${mention} `;

  return fullText.slice(0, atIndex) + insert + next;
}

export default function TournamentChatInput({
  className,
  id = 'message',
  placeholder,
  inputText,
  setInputText,
  onSendMessage,
  onFileSelect,
  uploadRef,
  sendMessageLoader,
  matchChatModeratorHelp,
}: MessageInputProps) {
  const [showPicker, setShowPicker] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)
  const [caretVersion, setCaretVersion] = useState(0);
const inputRef = useRef<HTMLInputElement>(null);
 const mentionRef = useRef<HTMLDivElement>(null);

const cursorPos = (() => {
  void caretVersion;
  return inputRef.current?.selectionStart ?? inputText.length;
})();

const activeMention = getActiveMentionQuery(inputText, cursorPos);
const mentionOpen = Boolean(matchChatModeratorHelp && activeMention);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value)
  }

  const insertMention = (mention: string) => {
  const el = inputRef.current;
  if (!el || !activeMention) return;

  const cursor = el.selectionStart ?? inputText.length;

  const next = applyMentionInsert(
    inputText,
    cursor,
    activeMention.atIndex,
    mention
  );

  setInputText(next);

  requestAnimationFrame(() => {
    const pos = activeMention.atIndex + mention.length + 1;
    el.focus();
    el.setSelectionRange(pos, pos);
  });
};

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (mentionOpen && event.key === "Enter") {
      event.preventDefault();
      insertMention(MENTION);
      return;
    }
    if (mentionOpen && event.key === "Escape") {
      event.preventDefault();
      inputRef.current?.blur();
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      handleSendClick();
    }
  };

  const handleSendClick = () => {
      onSendMessage(inputText)
    
  }

  const handleEmojiClick = (data: any) => {
    setInputText((prev) => prev + data?.emoji)
  }

  const onFileSelection = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    if (files.length && onFileSelect) {
      onFileSelect(files) 
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setShowPicker(false)
      }
    }

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showPicker])

  return (
    <div id={`${id}-input`} className={clsx("card-bracket", className)}>
      <div className={"container-bracekt"}>
        <div onClick={() => setShowPicker(!showPicker)}>
          <SmileEmoji fontSize={24} />
        </div>

        {showPicker && (
          <div className={"pickerWrapper"} ref={pickerRef}>
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              searchDisabled={true}
            />
          </div>
        )}

        <div className={"inputWrapper"}>
          {mentionOpen && activeMention && (
            <div className={styles.mentionDropdown} ref={mentionRef}>
              <button
                type="button"
                className={clsx(styles.mentionItem, styles.mentionItemActive)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(MENTION);
                }}
              >
                {MENTION}
              </button>
            </div>
          )}
          <input
            ref={inputRef}
            placeholder={placeholder ?? "Write a message..."}
            className={"input"}
            value={inputText}
            onKeyDown={onKeyDown}
            onChange={handleInputChange}
            onKeyUp={() => setCaretVersion((v) => v + 1)}
            onClick={() => setCaretVersion((v) => v + 1)}
            onSelect={() => setCaretVersion((v) => v + 1)}
          />
        </div>

        <div className={"rightButtons"}>
          <div onClick={() => uploadRef.current?.click()}>
            <AttachFile fontSize={24} />
          </div>
          {sendMessageLoader ? (
            <div onClick={handleSendClick}>
              <Loader />
            </div>
          ) : (
            <div onClick={handleSendClick}>
              <SendBold fontSize={'2.4rem'} />
            </div>
          )}
        </div>
      </div>

      <input
        onChange={onFileSelection}
        onClick={(event) => ((event.target as HTMLInputElement).value = '')}
        type='file'
        style={{ display: 'none' }}
        ref={uploadRef as React.Ref<HTMLInputElement>}
        accept='image/*'
        multiple
      />
    </div>
  )
}
